import { Button, Descriptions, Drawer, Table, Tag, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  ProTable,
  type ProFormInstance,
  type ProColumns,
} from '@ant-design/pro-components';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTenantDate } from '../lib/tenant';

type TransactionItem = {
  sampleId: string;
  sampleNo: string;
  barcode: string | null;
  materialName: string | null;
  passage: string | null;
  cellLineCode: string;
  fromStatus: string | null;
  toStatus: string;
  fromLocationId: string | null;
  toLocationId: string | null;
  fromLocationPath: string | null;
  toLocationPath: string | null;
};

type Transaction = {
  id: string;
  transactionNo: string;
  operationType: string;
  requestId: string | null;
  requestNo: string | null;
  executedBy: string;
  executedByName: string | null;
  executedAt: string;
  items: TransactionItem[];
};

const operationTypes = [
  'PUTAWAY',
  'CONSUME',
  'LOAN',
  'RETURN',
  'MOVE',
  'DISPOSE',
  'QUARANTINE',
  'CORRECT',
];
const allOperationTypes = 'ALL';

type StorageLocation = {
  id: string;
  path: string;
  displayPath?: string;
  type?: string;
};

type StorageTreeNode = {
  title: string;
  value: string;
  key: string;
  pathLabel: string;
  children?: StorageTreeNode[];
};

function buildStorageTreeData(source: StorageLocation[]): StorageTreeNode[] {
  const nodesByPath = new Map(source.map((item) => [item.path, item]));
  const roots: StorageTreeNode[] = [];
  const getChild = (
    siblings: StorageTreeNode[],
    value: string,
    title: string,
    pathLabel: string,
  ) => {
    let child = siblings.find((item) => item.value === value);
    if (!child) {
      child = { title, value, key: value, pathLabel, children: [] };
      siblings.push(child);
    }
    return child;
  };
  for (const location of source) {
    const pathParts = location.path.split('/').filter(Boolean);
    const displayParts = (location.displayPath ?? location.path)
      .split('/')
      .filter(Boolean);
    let siblings = roots;
    pathParts.forEach((part, index) => {
      const path = `/${pathParts.slice(0, index + 1).join('/')}`;
      const node = nodesByPath.get(path);
      const child = getChild(
        siblings,
        node?.id ?? `path:${path}`,
        displayParts[index] ?? part,
        displayParts.slice(0, index + 1).join(' / '),
      );
      if (index < pathParts.length - 1) siblings = child.children ?? [];
    });
  }
  return roots;
}

export function TransactionsPage() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Transaction>();
  const [storageLocations, setStorageLocations] = useState<StorageLocation[]>(
    [],
  );
  const formRef = useRef<ProFormInstance | undefined>(undefined);
  const [messageApi, holder] = message.useMessage();

  useEffect(() => {
    void fetch('/api/v1/storage-locations', { credentials: 'include' })
      .then(async (response) => {
        if (response.status === 401) {
          window.location.assign('/login');
          return;
        }
        if (!response.ok) throw new Error(t('transactions.storageLoadFailed'));
        const payload = (await response.json()) as
          | StorageLocation[]
          | { items?: StorageLocation[] };
        setStorageLocations(
          Array.isArray(payload) ? payload : (payload.items ?? []),
        );
      })
      .catch((error: unknown) => {
        void messageApi.error(
          error instanceof Error
            ? error.message
            : t('transactions.storageLoadFailed'),
        );
      });
  }, [messageApi, t]);

  const storageLocationTreeData = useMemo(
    () => buildStorageTreeData(storageLocations),
    [storageLocations],
  );
  const storageLocationTreeExpandedKeys = useMemo(
    () =>
      storageLocations
        .filter(
          (location) => location.type === 'SITE' || location.type === 'DEVICE',
        )
        .map((location) => location.id),
    [storageLocations],
  );

  const operationLabel = (value: string) => {
    const key = `transactions.operationTypes.${value}`;
    const translated = t(key);
    return translated === key ? value : translated;
  };

  const buildTransactionSearch = (params: Record<string, unknown>) => {
    const search = new URLSearchParams();
    const stringParams = [
      ['operationType', params.operationType],
      ['query', params.query],
      ['requestNo', params.requestNo],
      ['storageLocationId', params.storageLocationId],
    ] as const;
    for (const [key, value] of stringParams) {
      if (
        typeof value === 'string' &&
        value.trim() &&
        !(key === 'operationType' && value === allOperationTypes)
      )
        search.set(key, value.trim());
    }
    const dateRange = Array.isArray(params.executedAtRange)
      ? (params.executedAtRange as unknown[])
      : undefined;
    if (dateRange) {
      const from = dateRange[0];
      const to = dateRange[1];
      if (dayjs.isDayjs(from))
        search.set('executedFrom', from.format('YYYY-MM-DD'));
      if (dayjs.isDayjs(to)) search.set('executedTo', to.format('YYYY-MM-DD'));
    }
    return search;
  };

  const requestTransactions = async (params: Record<string, unknown>) => {
    const search = buildTransactionSearch(params);
    search.set('limit', '100');
    try {
      const response = await fetch(
        `/api/v1/inventory-transactions?${search.toString()}`,
        {
          credentials: 'include',
        },
      );
      if (response.status === 401) {
        window.location.assign('/login');
        return { data: [], success: false, total: 0 };
      }
      if (response.status === 403) throw new Error(t('transactions.forbidden'));
      if (!response.ok) throw new Error(t('transactions.loadFailed'));
      const payload = (await response.json()) as { items: Transaction[] };
      return {
        data: payload.items,
        success: true,
        total: payload.items.length,
      };
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('transactions.loadFailed'),
      );
      return { data: [], success: false, total: 0 };
    }
  };

  const exportTransactions = () => {
    const search = buildTransactionSearch(
      (formRef.current?.getFieldsValue?.() ?? {}) as Record<string, unknown>,
    );
    window.location.assign(
      `/api/v1/inventory-transactions/export.csv?${search.toString()}`,
    );
  };

  const columns: ProColumns<Transaction>[] = [
    {
      title: t('transactions.search'),
      dataIndex: 'query',
      hideInTable: true,
      fieldProps: {
        allowClear: true,
        placeholder: t('transactions.searchHint'),
      },
    },
    {
      title: t('transactions.operation'),
      dataIndex: 'operationType',
      valueType: 'select',
      valueEnum: Object.fromEntries(
        [allOperationTypes, ...operationTypes].map((value) => [
          value,
          {
            text:
              value === allOperationTypes
                ? t('transactions.allTypes')
                : operationLabel(value),
          },
        ]),
      ),
      initialValue: allOperationTypes,
      render: (_, row) => (
        <Tag color={row.operationType === 'CONSUME' ? 'orange' : 'blue'}>
          {operationLabel(row.operationType)}
        </Tag>
      ),
    },
    {
      title: t('transactions.storageFilter'),
      dataIndex: 'storageLocationId',
      valueType: 'treeSelect',
      hideInTable: true,
      fieldProps: {
        treeData: storageLocationTreeData,
        treeDefaultExpandedKeys: storageLocationTreeExpandedKeys,
        popupMatchSelectWidth: 280,
        treeLine: true,
        treeNodeFilterProp: 'title',
        treeNodeLabelProp: 'pathLabel',
        classNames: {
          popup: { root: 'transaction-location-tree-select' },
        },
        showSearch: true,
      },
    },
    {
      title: t('transactions.requestNo'),
      dataIndex: 'requestNo',
      hideInTable: true,
      fieldProps: {
        allowClear: true,
        placeholder: t('transactions.requestNoHint'),
      },
    },
    {
      title: t('transactions.executedAt'),
      dataIndex: 'executedAt',
      search: false,
      render: (_, row) => formatTenantDate(row.executedAt),
    },
    {
      title: t('transactions.executedAtRange'),
      dataIndex: 'executedAtRange',
      valueType: 'dateRange',
      hideInTable: true,
    },
    {
      title: t('transactions.operator'),
      dataIndex: 'executedByName',
      render: (_, row) => row.executedByName ?? row.executedBy,
    },
    {
      title: t('transactions.items'),
      dataIndex: 'items',
      search: false,
      render: (_, row) => row.items.length,
    },
    {
      title: t('transactions.samples'),
      dataIndex: 'sampleNos',
      search: false,
      render: (_, row) => {
        const sampleNos = row.items.map((item) => item.sampleNo);
        const visible = sampleNos.slice(0, 3).join('、');
        return sampleNos.length > 3 ? `${visible} …` : visible || '-';
      },
    },
    {
      title: t('transactions.cellLine'),
      dataIndex: 'cellLines',
      search: false,
      render: (_, row) => {
        const values = [...new Set(row.items.map((item) => item.cellLineCode))];
        const visible = values.slice(0, 3).join('、');
        return values.length > 3 ? `${visible} …` : visible || '-';
      },
    },
    {
      title: t('transactions.material'),
      dataIndex: 'materials',
      search: false,
      render: (_, row) => {
        const values = [
          ...new Set(
            row.items.map((item) => item.materialName).filter(Boolean),
          ),
        ] as string[];
        const visible = values.slice(0, 3).join('、');
        return values.length > 3 ? `${visible} …` : visible || '-';
      },
    },
    {
      title: t('transactions.passage'),
      dataIndex: 'passages',
      search: false,
      render: (_, row) => {
        const values = [
          ...new Set(row.items.map((item) => item.passage).filter(Boolean)),
        ] as string[];
        const visible = values.slice(0, 3).join('、');
        return values.length > 3 ? `${visible} …` : visible || '-';
      },
    },
    {
      title: t('transactions.actions'),
      key: 'actions',
      fixed: 'right',
      width: 90,
      search: false,
      render: (_, row) => (
        <a onClick={() => setSelected(row)}>{t('transactions.view')}</a>
      ),
    },
  ];

  const detailColumns: ColumnsType<TransactionItem> = [
    { title: t('transactions.sampleNo'), dataIndex: 'sampleNo' },
    {
      title: t('transactions.barcode'),
      dataIndex: 'barcode',
      render: (value: string | null) => value ?? '-',
    },
    { title: t('transactions.cellLine'), dataIndex: 'cellLineCode' },
    {
      title: t('transactions.material'),
      dataIndex: 'materialName',
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('transactions.passage'),
      dataIndex: 'passage',
      render: (value: string | null) => value ?? '-',
    },
    {
      title: t('transactions.fromStatus'),
      dataIndex: 'fromStatus',
      render: (value: string | null) => value ?? '-',
    },
    { title: t('transactions.toStatus'), dataIndex: 'toStatus' },
    {
      title: t('transactions.fromLocation'),
      dataIndex: 'fromLocationPath',
      render: (value: string | null, row) => value ?? row.fromLocationId ?? '-',
    },
    {
      title: t('transactions.toLocation'),
      dataIndex: 'toLocationPath',
      render: (value: string | null, row) => value ?? row.toLocationId ?? '-',
    },
  ];

  return (
    <section>
      {holder}
      <ProTable<Transaction>
        rowKey="id"
        columns={columns}
        request={requestTransactions}
        headerTitle={t('transactions.title')}
        formRef={formRef}
        toolBarRender={() => [
          <Button
            key="export"
            icon={<DownloadOutlined />}
            onClick={exportTransactions}
          >
            {t('transactions.export')}
          </Button>,
        ]}
        search={{ labelWidth: 'auto', defaultCollapsed: false }}
        cardBordered
        options={{
          reload: true,
          density: true,
          setting: true,
          fullScreen: true,
        }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
      />
      <Drawer
        title={selected?.transactionNo}
        open={Boolean(selected)}
        onClose={() => setSelected(undefined)}
        width={960}
      >
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          bordered
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label={t('transactions.operation')}>
            {selected ? operationLabel(selected.operationType) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('transactions.requestNo')}>
            {selected?.requestNo ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('transactions.operator')}>
            {selected?.executedByName ?? selected?.executedBy ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('transactions.executedAt')}>
            {formatTenantDate(selected?.executedAt)}
          </Descriptions.Item>
        </Descriptions>
        <Table
          rowKey="sampleId"
          size="small"
          dataSource={selected?.items ?? []}
          pagination={{ defaultPageSize: 20 }}
          columns={detailColumns}
          scroll={{ x: 'max-content' }}
        />
      </Drawer>
    </section>
  );
}
